import React, { Suspense } from 'react';
import { Lock } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { resolveWorkflowNodeEditStrategy } from './components/node/edit-strategies';
import { WorkflowNodeEditorReadOnlyProvider } from './components/node/edit-strategies/editor-read-only';
import {
  resolveGraphNodeRenderableNodeByViewData,
  useWorkflowRenderCatalog,
} from './workflow-render-catalog';
import { WorkflowEditPanel } from './components/workflow-edit-panel';
import { useWorkflowNodeDisplayOverride } from './components/node/workflow-node';
import { useWorkflowModel } from './workflow.model';
import type { WorkflowNodeViewData } from './components/node/types';

const { WorkflowNodeType } = PersistedWorkflow;

/**
 * Editors that read the *page* workflow model for their graph-derived sections
 * (for-each's body-node picker, run-workflow's target), not from the node alone.
 * Reused read-only they bind to the page workflow, so for a card that belongs to
 * a deeper nested workflow they would show that page workflow's data rather than
 * the card's — which is why {@link canReuseEditorReadOnly} declines them there.
 */
const MODEL_BOUND_EDITOR_TYPES: ReadonlySet<string> = new Set([
  WorkflowNodeType.ForEach,
  WorkflowNodeType.RunWorkflow,
]);

/**
 * Whether the inspector reuses the node's own config editor (shown inert) rather
 * than falling back to the card's summary.
 *
 * True when the type has an editor — with one exception: a
 * {@link MODEL_BOUND_EDITOR_TYPES} editor shown for a card *outside* the workflow
 * the hierarchy was opened from, where it would render the page workflow's
 * graph-derived sections instead of this card's. There we show the summary. For a
 * card in the page workflow (the common case) those editors bind correctly and
 * are reused like any other.
 */
export function canReuseEditorReadOnly(
  nodeType: string,
  inPageWorkflow: boolean
): boolean {
  if (resolveWorkflowNodeEditStrategy(nodeType) === null) {
    return false;
  }
  if (MODEL_BOUND_EDITOR_TYPES.has(nodeType) && !inPageWorkflow) {
    return false;
  }
  return true;
}

/** onChange sink for the read-only editors — a stable no-op, never persisted. */
const IGNORE_CHANGES = (): void => {};

/**
 * Read-only config for one hierarchy card, for walking someone through a
 * workflow without leaving the tree or being able to change it.
 *
 * Reuses the editor's own panel chrome and, where the per-type editor resolves
 * from the node alone, that same editor form — rendered inert (no mutation
 * wiring, no footer, pointer events off) so it reads as a snapshot. The two
 * model-bound editors (for-each, run-workflow) bind to the page's workflow, so
 * for a card in a *deeper* nested workflow they fall back to the card's summary
 * rather than show the wrong workflow's config; in the page workflow they are
 * reused like any other. See {@link canReuseEditorReadOnly}.
 */
export const NestedWorkflowNodeInspector: React.FC<{
  node: WorkflowNodeViewData;
  /** The workflow this card belongs to — the page model's for a top-level card. */
  ownerWorkflowId: string;
  onClose: () => void;
}> = ({ node, ownerWorkflowId, onClose }) => {
  const model = useWorkflowModel();
  const catalog = useWorkflowRenderCatalog();
  const renderStrategy = resolveGraphNodeRenderableNodeByViewData(
    catalog,
    node
  ).view;
  const editStrategy = resolveWorkflowNodeEditStrategy(node.type);
  const override = useWorkflowNodeDisplayOverride()?.(node);

  const title = override?.title ?? renderStrategy.getTitle(node);
  const subtitle =
    override?.description ?? renderStrategy.getDescription?.(node);

  // A model-bound editor (for-each, run-workflow) binds to the page workflow, so
  // it only resolves correctly for a card that belongs to it.
  const inPageWorkflow = ownerWorkflowId === model.workflowId;

  const body = canReuseEditorReadOnly(node.type, inPageWorkflow) ? (
    // The same editor the canvas shows, rendered inert: `inert` takes it out of
    // the tab order and the a11y tree and swallows every event, so a preview
    // can't be typed into or focused, and `onChange` is never persisted anyway.
    // `pointer-events-none` is the cursor cue; `opacity` reads as a preview, with
    // the banner above saying so outright. No disabled styling — the fields read
    // as the editor draws them.
    <div
      ref={(el) => {
        if (el !== null) {
          el.inert = true;
        }
      }}
      className="pointer-events-none select-none opacity-[0.85]"
    >
      <WorkflowNodeEditorReadOnlyProvider value={true}>
        <Suspense
          fallback={
            <Text.Caption className="text-muted-foreground block">
              Loading…
            </Text.Caption>
          }
        >
          {editStrategy !== null ? (
            <editStrategy.Editor
              key={node.id}
              node={node}
              onChange={IGNORE_CHANGES}
            />
          ) : null}
        </Suspense>
      </WorkflowNodeEditorReadOnlyProvider>
    </div>
  ) : (
    // No editor for this type, or a model-bound one for a card outside the page
    // workflow: show the card's own summary, the same body the tree draws.
    <div className="text-sm">{renderStrategy.renderBody(node)}</div>
  );

  return (
    <WorkflowEditPanel
      open
      onClose={onClose}
      icon={renderStrategy.renderIcon?.(node)}
      accent={renderStrategy.accent}
      badge={renderStrategy.badge}
      title={title}
      {...(subtitle !== undefined ? { subtitle } : {})}
    >
      {/* The panel is the editor's own chrome, so without this it reads as
          editable. A standing notice says it is not, and why. */}
      <div className="mb-4 flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
        <Lock className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
        <Text.Caption className="text-muted-foreground">
          Read-only preview. Open workflow to edit.
        </Text.Caption>
      </div>
      {body}
    </WorkflowEditPanel>
  );
};
